// Certificates list shown in the global Certificates tab and on closed session detail
import { useEffect, useMemo, useState } from 'react'
import { FileText, RotateCcw, Award, Download, Mail, MailWarning, ShieldOff, ShieldCheck, RefreshCw } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/ui/card'
import { Button } from '@/ui/button'
import { Badge } from '@/ui/badge'
import { useClassSessionStore, useCertificateStore, getCertificateStatus } from '@/features/classes/stores'
import type { SessionAttendee, Student } from '@/features/classes/stores'
import { useToast } from '@/core/hooks/useToast'

import { fmtDate } from '../utils/formatters'
import { openCertificatePDF } from '../utils/certificatePdf'
import { sendCertificateEmail, reRenderCertificate } from '../api/certificatesService'

interface CertificatesTabProps {
  sessionId?: string
}

export function CertificatesTab({ sessionId }: CertificatesTabProps) {
  const allAttendees = useClassSessionStore((s) => s.attendees)
  const students = useClassSessionStore((s) => s.students)
  const sessions = useClassSessionStore((s) => s.sessions)
  const courses = useClassSessionStore((s) => s.courses)
  const certificates = useClassSessionStore((s) => s.certificates)
  const loadCertificates = useClassSessionStore((s) => s.loadCertificates)
  const [loading, setLoading] = useState(true)
  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set())
  const [revokingCert, setRevokingCert] = useState<{ id: string; number: string; revoked: boolean } | null>(null)
  const [revokeReason, setRevokeReason] = useState('')
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set())
  const revokeCertificate = useCertificateStore((s) => s.revokeCertificate)
  const { show, ToastUI } = useToast()

  useEffect(() => {
    setLoading(true)
    void loadCertificates().finally(() => setLoading(false))
  }, [loadCertificates])

  const sessionAttendeeIds = useMemo(() => {
    if (!sessionId) {
      return null
    }
    return new Set(allAttendees.filter((a) => a.sessionId === sessionId).map((a) => a.id))
  }, [allAttendees, sessionId])

  async function handleResend(certId: string) {
    setSendingIds((prev) => new Set(prev).add(certId))
    try {
      await sendCertificateEmail(certId)
      show('Certificate email queued.', 'success')
      void loadCertificates()
    } catch (err) {
      show(err instanceof Error ? err.message : 'Failed to resend certificate email.', 'error')
    } finally {
      setSendingIds((prev) => {
        const next = new Set(prev)
        next.delete(certId)
        return next
      })
    }
  }

  async function handleRevoke() {
    if (!revokingCert) {return}
    if (!revokingCert.revoked && !revokeReason.trim()) {
      show('A reason is required to revoke a certificate.', 'error')
      return
    }
    setBusyIds((prev) => new Set(prev).add(revokingCert.id))
    try {
      await revokeCertificate(revokingCert.id, { revoked: !revokingCert.revoked, reason: revokeReason.trim() })
      show(revokingCert.revoked ? 'Certificate reinstated.' : 'Certificate revoked.', 'success')
      setRevokingCert(null)
      setRevokeReason('')
      void loadCertificates()
    } catch (err) {
      show(err instanceof Error ? err.message : 'Failed to update certificate.', 'error')
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev)
        next.delete(revokingCert.id)
        return next
      })
    }
  }

  async function handleReRender(certId: string) {
    setBusyIds((prev) => new Set(prev).add(certId))
    try {
      await reRenderCertificate(certId)
      show('Certificate re-rendered with current attendee data.', 'success')
      void loadCertificates()
    } catch (err) {
      show(err instanceof Error ? err.message : 'Failed to re-render certificate.', 'error')
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev)
        next.delete(certId)
        return next
      })
    }
  }

  const rows = useMemo(() => {
    return certificates
      .filter((cert) => !sessionAttendeeIds || sessionAttendeeIds.has(cert.attendeeId))
      .map((cert) => {
        const detail = cert.attendeeDetail
        const attendee = allAttendees.find((a) => a.id === cert.attendeeId)
        const student = attendee ? students.find((s) => s.id === attendee.studentId) : null
        const name =
          [detail?.firstName, detail?.lastName].filter(Boolean).join(' ').trim() ||
          [attendee?.firstName, attendee?.lastName].filter(Boolean).join(' ').trim() ||
          [student?.firstName, student?.lastName].filter(Boolean).join(' ').trim() ||
          'Unknown'
        const session = attendee
          ? sessions.find((s) => s.id === attendee.sessionId)
          : sessions.find((s) => s.id === detail?.sessionId)
        const course = session ? courses.find((c) => c.slug === session.courseId) : null
        const attendeeWithStudent: (SessionAttendee & { student: Student }) | null = attendee
          ? {
              ...attendee,
              student: {
                id: attendee.studentId,
                firstName: detail?.firstName ?? student?.firstName ?? attendee.firstName ?? 'Unknown',
                lastName: detail?.lastName ?? student?.lastName ?? attendee.lastName ?? '',
                email: detail?.email ?? student?.email ?? '',
                phone: detail?.phone ?? student?.phone ?? attendee.phone ?? '',
                company: detail?.company ?? student?.company ?? attendee.company ?? '',
              },
            }
          : null
        return {
          cert,
          name,
          attendeeWithStudent,
          courseName: course?.name ?? 'Course',
          courseSlug: course?.slug ?? 'fall-protection',
          instructorName: session?.instructorName ?? 'Jorge Ruiz',
        }
      })
  }, [certificates, sessionAttendeeIds, allAttendees, students, sessions, courses])

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-navy flex items-center gap-2">
          <Award className="w-4 h-4 text-primary" />
          Certificates ({loading ? '—' : rows.length})
        </CardTitle>
        <CardDescription>
          {sessionId
            ? 'Certificates issued when this class ended.'
            : 'All certificates issued through UBSafety.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <RotateCcw className="w-5 h-5 text-slate-150 animate-spin" />
            <p className="text-sm text-slate-400">Loading certificates…</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-center px-4">
            <FileText className="w-8 h-8 text-slate-200" />
            <p className="text-sm text-slate-500">
              {sessionId
                ? 'No certificates have been issued for this session.'
                : 'No certificates have been issued yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="border-b border-slate-150 bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Student</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Certificate Number</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Issued</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Email</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map(({ cert, name, attendeeWithStudent, courseName, courseSlug, instructorName }) => {
                  const status = getCertificateStatus(cert)
                  const badgeVariant = status === 'valid' ? 'success' : status === 'expired' ? 'warning' : 'destructive'
                  const emailSent = !!cert.emailSentAt
                  const emailFailed = !!cert.emailError
                  return (
                    <tr key={cert.id} className="border-b border-slate-150 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-navy">{name}</td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs">{cert.certificateNumber}</td>
                      <td className="px-4 py-3 text-slate-500">{fmtDate(cert.issuedAt)}</td>
                      <td className="px-4 py-3">
                        <Badge variant={badgeVariant} title={cert.revoked ? cert.revokedReason || undefined : undefined}>
                          {status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {emailSent ? (
                          <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                            <Mail className="w-3.5 h-3.5" />
                            Sent {fmtDate(cert.emailSentAt!)}
                          </span>
                        ) : emailFailed ? (
                          <span className="inline-flex items-center gap-1 text-red-500 text-xs" title={cert.emailError}>
                            <MailWarning className="w-3.5 h-3.5" />
                            Failed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-slate-400 text-xs">
                            <Mail className="w-3.5 h-3.5" />
                            Not sent
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setRevokeReason('')
                              setRevokingCert({ id: cert.id, number: cert.certificateNumber, revoked: cert.revoked })
                            }}
                            disabled={busyIds.has(cert.id)}
                            aria-label={cert.revoked ? 'Reinstate certificate' : 'Revoke certificate'}
                          >
                            {cert.revoked ? <ShieldCheck className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReRender(cert.id)}
                            disabled={busyIds.has(cert.id)}
                            aria-label="Re-render certificate with current attendee data"
                          >
                            <RefreshCw className={`w-4 h-4${busyIds.has(cert.id) ? ' animate-spin' : ''}`} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResend(cert.id)}
                            disabled={sendingIds.has(cert.id)}
                            aria-label="Resend certificate email"
                          >
                            {sendingIds.has(cert.id) ? (
                              <RotateCcw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4" />
                            )}
                          </Button>
                          <Button variant="outline" size="sm" asChild>
                            <a
                              href={`/certificate/${cert.verificationToken}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="View certificate (opens in new tab)"
                            >
                              View
                            </a>
                          </Button>
                          {attendeeWithStudent && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                openCertificatePDF(cert, attendeeWithStudent, courseName, courseSlug, instructorName)
                              }
                              aria-label="Download certificate PDF"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {ToastUI}
      {revokingCert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              if (!busyIds.has(revokingCert.id)) {setRevokingCert(null)}
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-navy">
              {revokingCert.revoked ? 'Reinstate certificate' : 'Revoke certificate'}
            </h3>
            <p className="text-sm text-slate-500 font-mono">{revokingCert.number}</p>
            {!revokingCert.revoked && (
              <>
                <label htmlFor="cert-revoke-reason" className="block text-xs font-medium text-slate-500 mb-1">Reason (required)</label>
                <textarea
                  id="cert-revoke-reason"
                  className="w-full rounded-lg border border-slate-200 p-2 text-sm"
                  rows={3}
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                />
                <p className="text-xs text-slate-400">
                  Revoked certificates no longer verify publicly. The certificate number and issue date are preserved.
                </p>
              </>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={busyIds.has(revokingCert.id)}
                onClick={() => setRevokingCert(null)}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={handleRevoke} disabled={busyIds.has(revokingCert.id)}>
                {revokingCert.revoked ? 'Reinstate' : 'Revoke certificate'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}
